<script setup lang="ts">
  import UserAvatar from '@/components/app/users/UserAvatar.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { useGlobalChatStore } from '@/store/app/globalChat/globalChatStore'
  import type {
    AiChatQuestionTemplateRequest,
    AIChatSessionMessage,
    CustomerCase,
    CustomerOrganisationUser,
    LawFieldEnum,
    LawTopicEnum
  } from '@/types/generated'
  import { AIChatSessionTypeEnum } from '@/types/generated'
  import { BookmarkIcon, LoaderCircle, RotateCw } from 'lucide-vue-next'
  import { computed, inject, onMounted, ref, type Ref } from 'vue'

  // Dialog components
  import InputField from '@/components/admin/form/InputField.vue'
  import SelectField, { type Option } from '@/components/admin/form/SelectField.vue'
  import TextAreaField from '@/components/admin/form/TextAreaField.vue'
  import { Button } from '@/components/ui/button'
  import { Dialog, DialogContent } from '@/components/ui/dialog'
  import { genericErrorToast, saveSuccessToast } from '@/lib/toasts'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { useLawFieldStore } from '@/store/lawFieldStore'
  import { useUserStore } from '@/store/userStore'
  import { AiChatQuestionTemplatesService } from '@/types/generated'

  const props = defineProps<{
    message: AIChatSessionMessage
  }>()

  const { t } = useTranslation({
    en: {
      SavePromptTemplate: 'Save Prompt Template',
      SaveMemory: 'Save memory',
      MemorySaved: 'Memory saved',
      Saved: 'Saved',
      TemplateTitle: 'Template Title',
      LawField: 'Law field',
      LawTopic: 'Law topic',
      Prompt: 'Prompt',
      PromptPlaceholder: 'Enter the prompt text that should be used as a template',
      PromptRequired: 'Prompt is required',
      description: 'Description',
      descriptionPlaceholder: 'Enter a description for this template (max 300 characters)',
      descriptionTooLong: 'Description is too long (max 300 characters)',
      generateWithAI: 'Generate with AI',
      generatingDescription: 'Generating description...',
      NotSelected: 'Not selected',
      selectLawField: 'Select law field...',
      selectLawTopic: 'Select law topic...',
      Cancel: 'Cancel',
      Save: 'Save',
      Saving: 'Saving...',
      characters: 'characters',
      TemplateVisibility: 'This template will be visible to all users in your lawfirm'
    },
    de: {
      SavePromptTemplate: 'Fragevorlage speichern',
      SaveMemory: 'Speichern',
      MemorySaved: 'Gespeichert',
      Saved: 'Gespeichert',
      TemplateTitle: 'Name',
      LawField: 'Rechtsgebiet',
      LawTopic: 'Rechtsthema',
      Prompt: 'Prompt',
      PromptPlaceholder: 'Geben Sie den Prompttext ein, der als Vorlage verwendet werden soll',
      PromptRequired: 'Prompt ist erforderlich',
      description: 'Beschreibung',
      descriptionPlaceholder:
        'Geben Sie eine Beschreibung für diese Vorlage ein (max. 300 Zeichen)',
      descriptionTooLong: 'Beschreibung ist zu lang (max. 300 Zeichen)',
      generateWithAI: 'Mit KI generieren',
      generatingDescription: 'Beschreibung wird generiert...',
      NotSelected: 'Nicht ausgewählt',
      selectLawField: 'Rechtsgebiet auswählen...',
      selectLawTopic: 'Rechtsthema auswählen...',
      Cancel: 'Abbrechen',
      Save: 'Speichern',
      Saving: 'Speichern...',
      characters: 'Zeichen',
      TemplateVisibility: 'Diese Vorlage wird für alle Benutzer in Ihrer Kanzlei sichtbar sein'
    }
  })

  const caseData = inject<Ref<CustomerCase | null>>('caseData', ref(null))
  const sessionType = inject<AIChatSessionTypeEnum>('sessionType', AIChatSessionTypeEnum.CASE)
  const globalChatStore = useGlobalChatStore()

  const user = computed<CustomerOrganisationUser | null>(() => {
    // For case context, get user from case data
    if (sessionType === AIChatSessionTypeEnum.CASE && caseData.value?.org?.users) {
      return caseData.value.org.users.find((user) => user.uuid === props.message.user_uuid) || null
    }

    // For global context, get user from organization users in global store
    if (sessionType === AIChatSessionTypeEnum.GLOBAL && props.message.user_uuid) {
      const orgUser = globalChatStore.getUserByUuid(props.message.user_uuid)

      if (orgUser) {
        return {
          uuid: orgUser.uuid,
          first_name: orgUser.first_name,
          last_name: orgUser.last_name,
          email: orgUser.email,
          avatar: orgUser.avatar,
          // Add other required fields with defaults
          role: 'USER' as any,
          is_active: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        } as unknown as CustomerOrganisationUser
      }
    }

    return null
  })

  const caseStore = useCaseStore()
  const userStore = useUserStore()
  // Dialog state
  const isSaveTemplateDialogOpen = ref(false)
  const templateTitle = ref('')
  const promptText = ref('')
  const descriptionText = ref('')
  const isGeneratingDescription = ref(false)
  const selectedLawField = ref('')
  const selectedLawTopic = ref('')
  const isSaving = ref(false)
  const isDataLoaded = ref(false)
  const DESCRIPTION_MAX_LENGTH = 300

  // Law field store access
  const { fetchLawFields, getLawTopicOptions, getLawFieldOptions } = useLawFieldStore()

  // Generate law field options from user store for both global and case contexts
  const lawFieldOptions = computed<Option[]>(() => {
    // For global context, use user store
    if (sessionType === AIChatSessionTypeEnum.GLOBAL) {
      if (!userStore.user) return []
      return getLawFieldOptions(userStore.user)
    }

    // For case context, prefer case data, fallback to user store
    if (caseData.value?.org?.law_fields) {
      return (caseData.value.org.law_fields as string[]).map((field) => ({
        value: field,
        title: field
      }))
    }

    // Fallback to user store
    if (!userStore.user) return []
    return getLawFieldOptions(userStore.user)
  })

  const lawTopicOptions = computed<Option[]>(() => {
    if (!selectedLawField.value) return []
    return getLawTopicOptions(selectedLawField.value)
  })

  const isTemplateTitleValid = computed(() => {
    return templateTitle.value.length > 0 && templateTitle.value.length <= 50
  })

  const isPromptValid = computed(() => {
    return promptText.value.length > 0
  })

  const isDescriptionValid = computed(() => {
    return (
      descriptionText.value.length <= DESCRIPTION_MAX_LENGTH && descriptionText.value.length > 0
    )
  })

  const isFormValid = computed(() => {
    return isTemplateTitleValid.value && isPromptValid.value && isDescriptionValid.value
  })

  // Load law fields data and ensure user data is loaded
  onMounted(async () => {
    try {
      // Ensure user data is loaded
      if (!userStore.user) {
        await userStore.fetchUser()
      }

      // Load law fields data
      await fetchLawFields()
      isDataLoaded.value = true
    } catch (error) {
      console.error('Failed to load data:', error)
    }
  })

  function handleLawFieldChange(value: string) {
    selectedLawTopic.value = ''
  }

  async function generateDescription() {
    if (!promptText.value) return

    isGeneratingDescription.value = true
    descriptionText.value = ''

    try {
      const response =
        await AiChatQuestionTemplatesService.aiChatQuestionTemplatesGenerateDescriptionCreate({
          requestBody: {
            prompt: promptText.value,
            law_field: selectedLawField.value,
            law_topic: selectedLawTopic.value
          }
        })

      // Simulate streaming effect for better UX
      const description = response.description
      let currentIndex = 0

      // Clear any previous content
      descriptionText.value = ''

      // Stream the description character by character
      const streamInterval = setInterval(() => {
        if (currentIndex < description.length) {
          descriptionText.value += description[currentIndex]
          currentIndex++
        } else {
          clearInterval(streamInterval)
          isGeneratingDescription.value = false
        }
      }, 15) // Adjust speed as needed
    } catch (error) {
      console.error('Failed to generate description:', error)
      isGeneratingDescription.value = false
    }
  }

  function handleDialogChange(isOpen: boolean) {
    if (!isOpen) {
      closeDialog()
    }
  }

  function closeDialog() {
    isSaveTemplateDialogOpen.value = false
    resetForm()
  }

  function resetForm() {
    templateTitle.value = ''
    promptText.value = ''
    descriptionText.value = ''
    selectedLawField.value = ''
    selectedLawTopic.value = ''
  }

  async function openSaveTemplateDialog() {
    try {
      // Ensure user data is loaded (needed for global context)
      if (!userStore.user) {
        await userStore.fetchUser()
      }

      // Ensure law fields are loaded before opening dialog
      if (!isDataLoaded.value) {
        await fetchLawFields()
        isDataLoaded.value = true
      }

      promptText.value = props.message.content
      templateTitle.value = ''
      descriptionText.value = ''
      selectedLawField.value = ''
      selectedLawTopic.value = ''
      isSaveTemplateDialogOpen.value = true
    } catch (error) {
      console.error('Error opening save template dialog:', error)
    }
  }

  async function saveTemplate() {
    if (!isFormValid.value) return

    isSaving.value = true

    try {
      await AiChatQuestionTemplatesService.aiChatQuestionTemplatesCreate({
        requestBody: {
          title: templateTitle.value,
          prompt: promptText.value,
          description: descriptionText.value,
          law_field: (selectedLawField.value as LawFieldEnum) || undefined,
          law_topic: (selectedLawTopic.value as LawTopicEnum) || undefined
        } as AiChatQuestionTemplateRequest
      })

      saveSuccessToast()
      isSaving.value = false

      // Reload templates based on context
      if (sessionType === AIChatSessionTypeEnum.GLOBAL) {
        // For global context, reload global prompt templates
        await globalChatStore.loadGlobalPromptTemplates()
      } else if (caseData.value?.uuid) {
        // For case context, reload case data to refresh templates
        await caseStore.loadCase(caseData.value.uuid)
      }

      closeDialog()
    } catch (error) {
      console.error('Error saving template:', error)
      genericErrorToast()
      isSaving.value = false
    }
  }
</script>

<template>
  <div class="ms-auto flex max-w-2xl justify-end gap-x-2 sm:gap-x-4">
    <div class="grow space-y-3 text-end">
      <div
        class="chat-bubble inline-block rounded-lg border border-purple-200 bg-purple-100 p-4 text-left shadow-sm"
        data-cy="user-message"
      >
        <p class="text-sm text-gray-800">
          {{ message.content }}
        </p>
      </div>
      <div class="flex justify-end gap-2">
        <!-- Save Template Button -->
        <button
          :class="[
            'rounded-md p-1.5 transition-colors',
            'text-gray-400 hover:bg-purple-50 hover:text-purple-600'
          ]"
          :title="t('SavePromptTemplate')"
          @click="openSaveTemplateDialog"
        >
          <div class="flex items-center gap-1">
            <BookmarkIcon class="h-5 w-5" />
            <span class="text-sm text-gray-500">{{ t('SavePromptTemplate') }}</span>
          </div>
        </button>
      </div>
    </div>
    <UserAvatar
      v-if="user"
      :user="user"
    />
  </div>

  <!-- Save Template Dialog -->
  <Dialog
    :open="isSaveTemplateDialogOpen"
    @update:open="handleDialogChange"
  >
    <DialogContent class="sm:max-w-[700px]">
      <h2 class="mb-0 text-lg font-semibold leading-none tracking-tight">
        {{ t('SavePromptTemplate') }}
      </h2>

      <div class="grid gap-1 py-1">
        <div class="grid gap-0.5">
          <InputField
            v-model="templateTitle"
            name="templateTitle"
            :label="t('TemplateTitle')"
            maxlength="50"
            :disabled="isSaving"
          />
          <div
            class="flex justify-end text-xs"
            :class="isTemplateTitleValid ? 'text-green-500' : 'text-red-500'"
          >
            {{ templateTitle.length }}/50 {{ t('characters') }}
          </div>
        </div>

        <div class="grid grid-cols-2 gap-1">
          <div class="grid gap-0.5">
            <SelectField
              v-model="selectedLawField"
              name="lawField"
              :label="t('LawField')"
              :options="lawFieldOptions"
              :disabled="isSaving"
              :placeholder="t('selectLawField')"
              @update:model-value="handleLawFieldChange"
            />
          </div>
          <div class="grid gap-0.5">
            <SelectField
              v-model="selectedLawTopic"
              name="lawTopic"
              :label="t('LawTopic')"
              :options="lawTopicOptions"
              :disabled="isSaving || !selectedLawField"
              :placeholder="t('selectLawTopic')"
            />
          </div>
        </div>

        <div class="mt-1 grid gap-0.5">
          <div class="relative">
            <TextAreaField
              v-model="promptText"
              label="Prompt"
              name="prompt"
              rows="6"
              :placeholder="t('PromptPlaceholder')"
              :disabled="isSaving"
              :error="!isPromptValid && promptText !== '' ? t('PromptRequired') : ''"
            />
          </div>
        </div>

        <!-- Description -->
        <div class="mt-3 grid gap-0.5">
          <div class="relative">
            <div class="mb-1 flex items-center justify-between">
              <label
                for="description"
                class="text-sm font-medium"
              >
                {{ t('description') }}
              </label>
              <button
                type="button"
                class="inline-flex items-center gap-1 rounded-md bg-purple-100 px-2 py-1 text-xs font-medium
                  text-purple-700 hover:bg-purple-200 disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="isSaving || isGeneratingDescription || !promptText"
                @click="generateDescription"
              >
                <span
                  v-if="isGeneratingDescription"
                  class="animate-spin"
                >
                  <LoaderCircle class="h-3 w-3" />
                </span>
                <span v-else>
                  <RotateCw class="h-3 w-3" />
                </span>
                {{ isGeneratingDescription ? t('generatingDescription') : t('generateWithAI') }}
              </button>
            </div>
            <TextAreaField
              v-model="descriptionText"
              name="description"
              rows="3"
              :placeholder="t('descriptionPlaceholder')"
              :disabled="isSaving || isGeneratingDescription"
              maxlength="300"
              :error="!isDescriptionValid ? t('descriptionTooLong') : ''"
            />
            <div
              class="mt-1 text-right text-xs"
              :class="!isDescriptionValid ? 'text-red-500' : 'text-green-500'"
            >
              {{ descriptionText.length }}/{{ DESCRIPTION_MAX_LENGTH }} {{ t('characters') }}
            </div>
          </div>
        </div>

        <p class="mt-0 text-xs text-gray-500">
          {{ t('TemplateVisibility') }}
        </p>
      </div>

      <div class="mt-3 flex gap-3">
        <button
          type="button"
          class="inline-flex h-10 flex-1 items-center justify-center rounded-md border border-input bg-background
            px-4 py-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent
            hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring
            focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
          :disabled="isSaving"
          @click="closeDialog"
        >
          {{ t('Cancel') }}
        </button>
        <Button
          type="submit"
          class="inline-flex h-10 flex-1 items-center justify-center rounded-md bg-purple-600 px-4 py-2 text-sm
            font-medium text-white ring-offset-background transition-colors hover:bg-purple-700
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2
            disabled:pointer-events-none disabled:opacity-50"
          :disabled="isSaving || !isFormValid"
          @click="saveTemplate"
        >
          <LoaderCircle
            v-if="isSaving"
            class="mr-2 h-4 w-4 animate-spin"
          />
          {{ isSaving ? t('Saving') : t('Save') }}
        </Button>
      </div>
    </DialogContent>
  </Dialog>
</template>
